import { formatClock } from '@/lib/actModes'
import { cn } from '@/lib/utils'

const MAX_SECONDS = 240

/**
 * How long he thinks the question will take, anywhere from nothing to four
 * minutes, to the second. A slider rather than a handful of preset buttons --
 * the whole point is that his guess gets finer as he gets better at it.
 */
export default function ActTimeSlider({
  value,
  onChange,
  referenceSeconds,
  referenceLabel,
}: {
  value: number
  onChange: (seconds: number) => void
  /** Section pace, marked on the track so he can see what he's up against. */
  referenceSeconds?: number | null
  referenceLabel?: string
}) {
  const nudge = (delta: number) => onChange(Math.min(MAX_SECONDS, Math.max(0, value + delta)))
  const referencePercent = referenceSeconds
    ? Math.min(100, (referenceSeconds / MAX_SECONDS) * 100)
    : null

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => nudge(-5)}
          disabled={value <= 0}
          className="w-12 h-12 rounded-full border-2 border-border text-lg font-bold hover:bg-accent transition disabled:opacity-30"
          aria-label="Five seconds less"
        >
          −
        </button>
        <div className="text-center min-w-[7rem]">
          <p className="text-5xl font-mono font-bold tabular-nums tracking-tight">{formatClock(value)}</p>
          <p className="text-xs text-muted-foreground">your call</p>
        </div>
        <button
          type="button"
          onClick={() => nudge(5)}
          disabled={value >= MAX_SECONDS}
          className="w-12 h-12 rounded-full border-2 border-border text-lg font-bold hover:bg-accent transition disabled:opacity-30"
          aria-label="Five seconds more"
        >
          +
        </button>
      </div>

      <div className="relative pt-1">
        <input
          type="range"
          min={0}
          max={MAX_SECONDS}
          step={1}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="w-full accent-primary h-11"
          aria-label="How long you think this question will take, in seconds"
          aria-valuetext={`${value} seconds`}
        />
        {referencePercent != null && (
          <div
            className="absolute -bottom-0.5 w-px h-3 bg-primary/60 pointer-events-none"
            style={{ left: `${referencePercent}%` }}
            aria-hidden="true"
          />
        )}
      </div>

      <div className={cn('flex justify-between text-xs text-muted-foreground', !referenceSeconds && 'pt-1')}>
        <span>0s</span>
        {referenceSeconds != null && (
          <span className="text-primary font-medium">
            {referenceLabel || 'test pace'} {formatClock(referenceSeconds)}
          </span>
        )}
        <span>4:00</span>
      </div>
    </div>
  )
}
